import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '@/shared/api/client';
import { LoadingSpinner } from '@/shared/components/ui/LoadingSpinner';

interface PublicVerification {
  verification_id: string;
  verification_timestamp: string;
  status: string;
  message: string;
  legal_implication: string;
  original_filename?: string;
  certificate?: {
    subject: string;
    issuer: string;
    valid_from: string;
    valid_to: string;
    serial_number: string;
  };
  hash: {
    algorithm: string;
    value: string;
  };
  regulatory_framework?: {
    country: string;
    name: string;
  };
  pki_failure?: {
    reason: string;
    message: string;
  };
  signature_timestamp?: string;
}

const STATUS_CONFIG = {
  FIRMA_DIGITAL_VALIDA: {
    icon: '✅',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-900/30',
    borderColor: 'border-green-500 dark:border-green-600',
    title: 'Firma Digital Válida'
  },
  FIRMA_ELECTRONICA_DETECTADA: {
    icon: '⚠️',
    color: 'text-yellow-600 dark:text-yellow-300',
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/40',
    borderColor: 'border-yellow-500 dark:border-yellow-600',
    title: 'Firma Electrónica Detectada'
  },
  SIN_FIRMA_VALIDA: {
    icon: '❌',
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-50 dark:bg-gray-800/50',
    borderColor: 'border-gray-500 dark:border-gray-600',
    title: 'Sin Firma Válida'
  },
  ERROR: {
    icon: '⚠️',
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/30',
    borderColor: 'border-red-500 dark:border-red-600',
    title: 'Error en Verificación'
  }
} as const;

export default function VerificationLookup() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [verification, setVerification] = useState<PublicVerification | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const verificationUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(verificationUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.createElement('input');
      input.value = verificationUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  useEffect(() => {
    const fetchVerification = async () => {
      if (!id) {
        setError('ID de verificación no proporcionado');
        setLoading(false);
        return;
      }

      try {
        const response = await apiClient.get(`/api/verify/${id}`);
        setVerification(response.data);
      } catch (err: any) {
        if (err.status_code === 404) {
          setError('Verificación no encontrada. El ID proporcionado no existe en nuestros registros.');
        } else {
          setError('Error al cargar la verificación. Por favor, intente nuevamente.');
        }
        console.error('Error fetching verification:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchVerification();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-legal-900">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !verification) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-legal-900 p-4">
        <div className="max-w-md w-full bg-white dark:bg-legal-800 rounded-lg shadow-lg p-8">
          <div className="text-center">
            <span className="text-6xl">❌</span>
            <h2 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">Error</h2>
            <p className="mt-2 text-gray-600 dark:text-legal-300">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="mt-6 px-6 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
            >
              Volver al Inicio
            </button>
          </div>
        </div>
      </div>
    );
  }

  const config = STATUS_CONFIG[verification.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.ERROR;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-legal-900 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Validación Pública de Firma</h1>
          <p className="mt-2 text-gray-600 dark:text-legal-300">Consulta independiente de verificación</p>
        </div>

        {/* Main Card */}
        <div className={`bg-white dark:bg-legal-800 rounded-lg shadow-lg border-l-4 ${config.borderColor} p-6 mb-6`}>
          {/* Status Header */}
          <div className="flex items-center gap-3 mb-6">
            <span className="text-5xl">{config.icon}</span>
            <div>
              <h2 className={`text-2xl font-bold ${config.color}`}>{config.title}</h2>
              <p className="text-sm text-gray-500 dark:text-legal-400">
                Verificado el {new Date(verification.verification_timestamp).toLocaleString('es-AR')}
              </p>
            </div>
          </div>

          {/* Filename */}
          {verification.original_filename && (
            <div className="mb-4">
              <p className="text-legal-600 dark:text-legal-400 text-sm font-mono">
                Archivo verificado: {verification.original_filename}
              </p>
            </div>
          )}

          {/* Message */}
          <div className={`${config.bgColor} rounded-lg p-4 mb-4`}>
            <p className={`font-medium ${config.color}`}>{verification.message}</p>
          </div>

          {/* Legal Implication */}
          <div className="bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500 p-4 mb-6">
            <h3 className="font-bold text-blue-900 dark:text-blue-300 mb-2">Implicancia Legal (Argentina - Ley 25.506)</h3>
            <p className="text-blue-800 dark:text-blue-200">{verification.legal_implication}</p>
          </div>

          {/* Verification ID */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-700 dark:text-legal-300 mb-2">ID de Verificación</h3>
            <code className="block bg-gray-100 dark:bg-legal-700 text-gray-900 dark:text-legal-100 p-3 rounded text-sm break-all font-mono">
              {verification.verification_id}
            </code>
          </div>

          {/* Hash */}
          {verification.hash && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-700 dark:text-legal-300 mb-2">Hash del Documento ({verification.hash.algorithm})</h3>
              <code className="block bg-gray-100 dark:bg-legal-700 text-gray-900 dark:text-legal-100 p-3 rounded text-sm break-all font-mono">
                {verification.hash.value}
              </code>
            </div>
          )}

          {/* Certificate Info */}
          {verification.certificate && (
            <div className="mb-6 border-t border-gray-200 dark:border-legal-700 pt-6">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4">Información del Certificado Digital</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-700 dark:text-legal-300 text-sm">Sujeto</h4>
                  <p className="text-gray-600 dark:text-legal-400 text-sm">{verification.certificate.subject}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 dark:text-legal-300 text-sm">Emisor</h4>
                  <p className="text-gray-600 dark:text-legal-400 text-sm">{verification.certificate.issuer}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 dark:text-legal-300 text-sm">Válido Desde</h4>
                  <p className="text-gray-600 dark:text-legal-400 text-sm">
                    {new Date(verification.certificate.valid_from).toLocaleString('es-AR')}
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 dark:text-legal-300 text-sm">Válido Hasta</h4>
                  <p className="text-gray-600 dark:text-legal-400 text-sm">
                    {new Date(verification.certificate.valid_to).toLocaleString('es-AR')}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <h4 className="font-semibold text-gray-700 dark:text-legal-300 text-sm">Número de Serie</h4>
                  <p className="text-gray-600 dark:text-legal-400 text-sm font-mono">{verification.certificate.serial_number}</p>
                </div>
              </div>
            </div>
          )}

          {/* Regulatory Framework */}
          {verification.regulatory_framework && (
            <div className="mb-6 border-t border-gray-200 dark:border-legal-700 pt-6">
              <h3 className="font-bold text-gray-900 dark:text-white mb-2">Marco Regulatorio</h3>
              <p className="text-gray-700 dark:text-legal-300">
                <span className="font-semibold">País:</span> {verification.regulatory_framework.country}
              </p>
              <p className="text-gray-700 dark:text-legal-300">
                <span className="font-semibold">Marco Legal:</span> {verification.regulatory_framework.name}
              </p>
            </div>
          )}

          {/* PKI Failure */}
          {verification.pki_failure && (
            <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-600 rounded-lg p-4">
              <h3 className="font-bold text-yellow-900 dark:text-yellow-300 mb-2">Advertencia PKI</h3>
              <p className="text-yellow-800 dark:text-yellow-200">
                <span className="font-semibold">Razón:</span> {verification.pki_failure.reason}
              </p>
              <p className="text-yellow-800 dark:text-yellow-200 mt-1">{verification.pki_failure.message}</p>
            </div>
          )}
        </div>

        {/* URL de verificación */}
        <div className="bg-white dark:bg-legal-800 rounded-lg shadow-lg p-6 mb-6">
          <h3 className="font-semibold text-gray-700 dark:text-legal-300 mb-2">
            URL de verificación
          </h3>
          <p className="text-sm text-gray-500 dark:text-legal-400 mb-3">
            Usa esta URL para volver a consultar esta verificación en cualquier momento.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-gray-100 dark:bg-legal-700 text-gray-900 dark:text-legal-100 px-3 py-2 rounded text-sm break-all font-mono">
              {verificationUrl}
            </code>
            <button
              onClick={handleCopy}
              className={`flex-shrink-0 px-4 py-2 rounded-lg transition-colors font-medium text-sm ${
                copied
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50'
              }`}
            >
              {copied ? 'Copiado' : 'Copiar'}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors font-medium"
          >
            Verificar Otro Documento
          </button>
        </div>

        {/* Info Footer */}
        <div className="mt-8 text-center text-sm text-gray-500 dark:text-legal-400">
          <p>Esta verificación es pública y puede ser consultada por cualquier persona con el ID de verificación.</p>
          <p className="mt-2">SignWiz - Evidencia Digital Inteligente</p>
          <p className="mt-2">Desarrollado por <a href="https://arteclaw.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">Arteclaw</a></p>
        </div>
      </div>
    </div>
  );
}
